import { useCallback, useReducer } from 'react';

import { Overlay, Portal } from 'ui/components/primitives';

import type { DrawerProps } from '../Drawer';
import { DrawerTemplate } from '../components';

import type { DrawerState, DrawerStateUpdate } from './StackNavigatorProvider.d';
import { ACTION } from './StackNavigatorProvider.d';
import { DrawerContext, useDrawerEventListeners } from './StackNavigatorProvider.hooks';
import { drawerInitialState, drawerReducer } from './StackNavigatorProvider.reducer';

const StackNavigatorProvider = ({ children }: DrawerProps) => {
  const [state, dispatch] = useReducer(drawerReducer, drawerInitialState);
  const { drawerStack, hasOpenDrawers } = state;

  const closeAllDrawers = useCallback(() => {
    dispatch({ type: ACTION.CLOSE_ALL_DRAWERS, payload: '' });
  }, []);

  const popStateFromStack = useCallback((id: string) => {
    dispatch({ type: ACTION.POP_STATE_FROM_DRAWER_STACK, payload: id });
  }, []);

  const pushStateToStack = useCallback((stackDrawer: DrawerState) => {
    dispatch({ type: ACTION.PUSH_STATE_TO_DRAWER_STACK, payload: stackDrawer });
  }, []);

  const updateDrawerStack = useCallback((stackDrawer: DrawerStateUpdate) => {
    dispatch({ type: ACTION.UPDATE_DRAWER_STACK, payload: stackDrawer });
  }, []);

  useDrawerEventListeners(hasOpenDrawers, closeAllDrawers);

  const value = {
    closeAllDrawers,
    drawerStack,
    hasOpenDrawers,
    popStateFromStack,
    pushStateToStack,
    updateDrawerStack,
  };

  return (
    <DrawerContext.Provider value={value}>
      {children}

      {drawerStack.map((props) => (
        <DrawerTemplate key={props.id} onDismiss={closeAllDrawers} {...props} />
      ))}

      <Portal>
        <Overlay onClick={closeAllDrawers} visible={hasOpenDrawers} />
      </Portal>
    </DrawerContext.Provider>
  );
};

export default StackNavigatorProvider;
